import { useState } from "react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  containerClassName?: string;
  /** Priority loading for above-the-fold images */
  priority?: boolean;
  /** Optional width for sizing hints */
  width?: number;
  /** Optional height for sizing hints */
  height?: number;
  /** Use WebP format with automatic fallback */
  useWebP?: boolean;
}

const FALLBACK_IMAGE = "/placeholder.svg";

/**
 * Helper to generate WebP URL for Supabase Storage images
 * Supabase Storage supports on-the-fly image transformation
 */
export const getWebPUrl = (src: string): string | null => {
  if (!src) return null;
  
  // DISABLED: Supabase Image Transformations requires Pro plan
  // Images from Supabase Storage are served in original format
  if (src.includes('supabase.co/storage')) {
    return null;
  }
  
  // For local assets, check if WebP version might exist
  if (src.match(/\.(jpg|jpeg|png)$/i)) {
    return src.replace(/\.(jpg|jpeg|png)$/i, '.webp');
  }
  
  return null;
};

const OptimizedImage = ({ 
  src, 
  alt, 
  className, 
  containerClassName,
  priority = false,
  width,
  height,
  useWebP = true
}: OptimizedImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLandscape, setIsLandscape] = useState(true);
  const [hasError, setHasError] = useState(false);

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setIsLandscape(img.naturalWidth >= img.naturalHeight);
    setIsLoaded(true);
  };

  const handleError = () => {
    setHasError(true);
    setIsLoaded(true);
  };

  const imageSrc = hasError ? FALLBACK_IMAGE : src;
  const webpSrc = useWebP && !hasError ? getWebPUrl(src) : null;

  return (
    <div className={cn("relative flex items-center justify-center overflow-hidden", containerClassName)}>
      {!isLoaded && (
        <Skeleton className="absolute inset-0" />
      )}
      
      <picture>
        {webpSrc && (
          <source srcSet={webpSrc} type="image/webp" />
        )}
        <img
          src={imageSrc}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          fetchPriority={priority ? "high" : "auto"}
          width={width}
          height={height}
          className={cn(
            "transition-opacity duration-300",
            isLoaded ? "opacity-100" : "opacity-0",
            isLandscape ? "w-full h-auto" : "h-full w-auto",
            className
          )}
        />
      </picture>
    </div>
  );
};

export { OptimizedImage };
